<template>
  <Tooltip v-if="disabled" position="left">
    <template v-slot:text>
      <Button severity="secondary" :disabled="disabled">
        <IconPlus />
        Create Cluster
      </Button>
    </template>
    <template v-slot:tooltip> Select zones to add </template>
  </Tooltip>

  <template v-else>
    <Button severity="secondary" @click="visible = true" :disabled="disabled">
      <IconPlus />
      Create Cluster
    </Button>
  </template>

  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    @onHide="onHide"
    @onClose="closeModal"
    class="p-dialog-breathe"
  >
    <template #title>
      Create Cluster
      <Tooltip position="left">
        <template v-slot:text>
          <IconInfo />
        </template>
        <template v-slot:tooltip>
          A Cluster is a collection of one or more zones. Visitor insights and metrics will be
          provided at a cluster-level. For more details head to the FAQ page.
        </template>
      </Tooltip>
    </template>

    <template #body>
      <div class="modal-body-add-zone modal-body-add-cluster">
        <DialogClusterForm
          v-if="stage === 1"
          v-model:cluster="newCluster"
          v-model:newEvent="newEvent"
          @changeStage="goToStageTwo"
        />

        <DialogClusterZones
          v-if="stage === 2"
          v-model:cluster="newCluster"
          v-model:newEvent="newEvent"
          @changeStage="goToStageOne"
        />
      </div>
    </template>

    <template #footer>
      <Button
        v-if="stage === 1"
        :disabled="loading || !clusterValid"
        label="Save Changes"
        severity="secondary"
        @click="saveModal()"
        autofocus
      />

      <Button
        v-if="stage === 2"
        :disabled="loading"
        label="Done"
        severity="primary"
        @click="stage = 1"
        autofocus
      />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import type { EventTypeNew, ZoneType, ClusterType } from '@/types/Event';

import { Button } from 'primevue';

import DialogFrame from './DialogFrame.vue';
import DialogClusterForm from './forms/DialogClusterForm.vue';
import DialogClusterZones from './forms/DialogClusterZones.vue';

import Tooltip from '@/components/global/Tooltip.vue';

import IconInfo from '@/icons/IconInfo.svg?component';
import IconPlus from '@/icons/IconPlus.svg?component';

const newEvent = defineModel<EventTypeNew>('newEvent');

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    defaultZones?: ZoneType[];
  }>(),
  {
    disabled: false,
  },
);

const newCluster = ref<ClusterType>({
  name: '',
  categories: [],
  zones: [],
});

const emit = defineEmits(['onClusterAdded']);

const visible = ref(false);
const loading = ref(false);
const stage = ref(1);

watch(visible, (newVal) => {
  if (newVal === true) {
    if (props.defaultZones) {
      newCluster.value.zones = props.defaultZones;
    }
  }
});

const saveModal = () => {
  emit('onClusterAdded', newCluster.value);
  onHide();
};

const closeModal = () => {
  onHide();
};

const onHide = () => {
  stage.value = 1;
  visible.value = false;
  newCluster.value = {
    name: '',
    categories: [],
    zones: [],
  };
};

const hasUniqueName = computed(() => {
  let nameUnique = true;

  newEvent.value?.clusters.forEach((cluster) => {
    if (cluster.name.toLowerCase().trim() === newCluster.value.name.toLowerCase().trim()) {
      nameUnique = false;
    }
  });

  return nameUnique;
});

const clusterValid = computed(() => {
  const hasName = newCluster.value.name.trim() !== '';
  const hasType = newCluster.value.categories.length > 0;
  const hasZones = newCluster.value.zones.length > 0;
  return hasName && hasType && hasZones && hasUniqueName.value;
});

const goToStageTwo = () => {
  stage.value = 2;
};

const goToStageOne = () => {
  stage.value = 1;
};
</script>
